/*
 * ParticipantListItem.jsx 05.12.2017
 *
 * (c) 2017 ergovia GmbH
 */
import React, {Fragment} from 'react';

import ErrorIcon from '@material-ui/icons/Error';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CheckIcon from '@material-ui/icons/Check';
import CircularProgress from '@material-ui/core/CircularProgress';
import TableCell from '@material-ui/core/TableCell';
import Tooltip from '@material-ui/core/Tooltip';
import {withStyles} from '@material-ui/core/styles';
import {countDeadlineExceeded, countNearDeadline, hasGaps, hasRelevantOpenGoalsForToday} from '../../utils';
import {COLOR_CODE_NEGATIVE, COLOR_CODE_NEUTRAL, COLOR_CODE_POSITIVE, COLOR_CODE_WARNING, TEXT_COLOR_INVERSE} from '../../styles/colors';

const styles = theme => ({

    rootDefault: {
        textAlign: 'center',
        borderBottom: '0'
    },
    paddingDefault: {
        padding: '17px 0px',
        textAlign: 'center'
    },
    tooltip: {
        background: COLOR_CODE_NEUTRAL,
        color: COLOR_CODE_NEGATIVE,
        fontSize: 'small'
    },
    checkTooltip: {
        color: TEXT_COLOR_INVERSE
    },
    warningTooltip: {
        color: TEXT_COLOR_INVERSE
    },
    hintTooltip: {
        color: TEXT_COLOR_INVERSE
    },
    progress: {
        margin: '-2px 9px -2px 0px',
        color: theme.palette.text.primary
    }
});


/**
 * Komponente zum Anzeigen eines Incons für Erfolgsmeldungen.
 * Per Tooltip kann ein zusätzlicher Text angezeigt werden (zu setzen über das 'message'-Property).
 */
const CheckSymbol = withStyles(styles)(props => {

    const {classes, message} = props;

    return (
        <Tooltip placement="right" enterDelay={20} leaveDelay={5} title={message || ''} classes={{tooltip: `${classes.tooltip} ${classes.checkTooltip}`}}>
            <CheckIcon style={{margin: '-2px 9px -2px 0px', color: COLOR_CODE_POSITIVE}}/>
        </Tooltip>
    );
});

/**
 * Komponente zum Anzeigen eines Icons für Warnmeldungen.
 * Per Tooltip kann ein zusätzlicher Text angezeigt werden (zu setzen über das 'message'-Property).
 */
const WarningSymbol = withStyles(styles)(props => {

    const {classes, message} = props;

    return (
        <Tooltip placement="right" enterDelay={50} title={message || ''} classes={{tooltip: `${classes.tooltip} ${classes.warningTooltip}`}}>
            <ErrorIcon style={{margin: '-2px 9px -2px 0px', color: COLOR_CODE_NEGATIVE}}/>
        </Tooltip>
    );
});

/**
 * Komponente zum Anzeigen eines Icons für Hinweismeldungen.
 * Per Tooltip kann ein zusätzlicher Text angezeigt werden (zu setzen über das 'message'-Property).
 */
const HintSymbol = withStyles(styles)(props => {

    const {classes, message} = props;

    return (
        <Tooltip placement="right" enterDelay={50} title={message || ''} classes={{tooltip: `${classes.tooltip} ${classes.hintTooltip}`}}>
            <ErrorOutlineIcon style={{margin: '-2px 9px -2px 0px', color: COLOR_CODE_WARNING}}/>
        </Tooltip>
    );
});


/**
 * Private Subkomponente zur Darstellung der Fristanzeige
 */
class FristSymbol extends React.Component {

    render() {

        const {goals, classes} = this.props;

        if (goals === null) {
            return <CircularProgress className={classes.progress} size={24} thickness={4}/>;
        }

        if (goals.length === 0) {
            return null;
        }

        if (countDeadlineExceeded(goals) > 0) {
            return <WarningSymbol message="Die Zielfrist ist überschritten!"/>;
        }

        if (countNearDeadline(goals) > 0) {
            return <HintSymbol message="Die Zielfrist ist bald erreicht!"/>;
        }

        return <CheckSymbol/>;
    }
}


/**
 * Private Subkomponente zur Darstellung der Zielanzeige
 */
class ZielSymbol extends React.Component {

    render() {
        const {goals, classes} = this.props;

        if (goals === null) {
            return <CircularProgress className={classes.progress} size={24} thickness={4}/>;
        }

        if (goals.length > 0) {

            if (!hasRelevantOpenGoalsForToday(goals)) {
                return <WarningSymbol message="Es muss ein neues Ziel angelegt werden!"/>;
            }

            if (hasGaps([...goals])) {
                return <WarningSymbol message="Es gibt Zeiträume ohne Ziele!"/>;
            }

            return <CheckSymbol/>;
        }

        return <WarningSymbol message="Es ist kein Ziel angelegt!"/>;
    }
}

/**
 * Private Subkomponente zur Darstellung der Schritte
 */
class SchrittSymbol extends React.Component {

    render() {
        const {goals, classes} = this.props;

        if (goals === null) {
            return <CircularProgress className={classes.progress} size={24} thickness={4}/>;
        }

        if (goals.length > 0) {

            if (goals.filter(g => g.schrittVorhanden).length === goals.length) {
                return <CheckSymbol/>;
            }
            return <WarningSymbol message="Bei mindestens einem Ziel ist kein Schritt hinterlegt!"/>;

        }

        return null;
    }
}

/**
 * Komponente für einen einzelenen Teilnehmer in einer Liste. Zeigt den Teilnehmernamen und seinee Handlungsbedarfe an.
 */
class ParticipantListItem extends React.Component {

    /**
     * Registriert einen Listener "focus": Wenn das Browser-Fenster in den Vordergrund geholt wird,
     * dann sollen die Teilnehmerdaten in der Liste aktualisiert werden.
     */
    constructor() {
        super();
        this.focusListener = this.refetchData.bind(this);
        window.addEventListener('focus', this.focusListener);
    }

    /**
     * Deregistriert den Listener auf das Browser-Fenster, wenn die Komponente abgeräumt wird.
     */
    componentWillUnmount() {
        window.removeEventListener('focus', this.focusListener);
    }

    /**
     * Löst einen Apollo-Refetch der Teilnehmerdaten aus.
     */
    refetchData() {
        this.props.refetchData();
    }

    render() {
        const {data, classes} = this.props;
        const ziele = data !== null ? data.ziele : null;

        return (
            <Fragment>
                <TableCell padding="none" classes={{paddingNone: classes.paddingDefault, root: classes.rootDefault}}>
                    <ZielSymbol goals={ziele} classes={classes}/>
                </TableCell>
                <TableCell padding="none" classes={{paddingNone: classes.paddingDefault, root: classes.rootDefault}}>
                    <FristSymbol goals={ziele} classes={classes}/>
                </TableCell>
                <TableCell padding="none" classes={{paddingNone: classes.paddingDefault, root: classes.rootDefault}}>
                    <SchrittSymbol goals={ziele} classes={classes}/>
                </TableCell>
            </Fragment>
        );
    }
}

export default withStyles(styles)(ParticipantListItem);



// WEBPACK FOOTER //
// ./src/de/ergovia/components/participants/ParticipantListItem.jsx